import React, { Dispatch } from 'react';
import { FormControl, Stack } from '@mui/material';
import StyledModal from '../../../Components/GenericModal/StyledModal';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import Api from '../../API';
import { ApplicationAction, ApplicationState, IApplication, IElmoExportParams } from '../../types';
import { ModalType } from '../../config';
import ModalFooterButtons from '../../../Components/GenericModal/ModalFooterButtons';
import { styles } from '../styles';
import { FormNumberField, FormTextField } from '../../../Components/CustomUIElements/FormTextField';
import { useDispatch } from '../../../hooks/useDispatch';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSelectField from '../../../Components/CustomUIElements/FormSelectField';

interface IElmoDepartment {
  id: string;
  department_id: string;
  title: string;
  description: string;
}

const elmoFormSchema = z.object({
  firstname: z.string().min(1, 'First name cannot be empty'),
  lastname: z.string().min(1, 'Last name cannot be empty'),
  email: z.string().email('Please enter a proper email address'),
  phone1: z.string().min(1, 'Please enter a mobile number'),
  department: z.string().min(1, 'Please select a department')
});

type ElmoFormData = z.infer<typeof elmoFormSchema>;

export default function Elmo({
  ApplicationState,
  dispatch
}: {
  ApplicationState: ApplicationState;
  dispatch: Dispatch<ApplicationAction>;
}) {
  const { modalsOpen } = ApplicationState;
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);
  const { displayError, displaySuccess } = useDispatch(dispatch);

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const { res } = await Api.getElmoDepartments();
      return res as unknown as IElmoDepartment[];
    },
    enabled: modalsOpen === ModalType.ELMO
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ElmoFormData>({
    resolver: zodResolver(elmoFormSchema),
    defaultValues: {
      firstname: application?.candidate?.firstname || '',
      lastname: application?.candidate?.lastname || '',
      email: application?.candidate?.email || '',
      phone1: application?.candidate?.phone1 || '',
      department: application?.candidate?.department || ''
    }
  });

  const { mutate: elmoExport, isLoading: exportingElmo } = useMutation({
    mutationFn: async (data: ElmoFormData): Promise<{ success: string }> => {
      if (application) {
        const params: IElmoExportParams = {
          first_name: data.firstname,
          surname: data.lastname,
          email: data.email,
          mobile: data.phone1,
          department: data.department
        };
        const { res } = await Api.elmoExport(application.id, params);
        return res as { success: string };
      }
      throw new Error('Application not found');
    },
    onSuccess: (res: { success: string }) => {
      displaySuccess(res.success);
      handleClose();
    },
    onError: (error: { error: string }) => {
      displayError(`There was an error in exporting to Elmo, ${error.error}`);
    }
  });

  const handleClose = () => {
    reset();
    dispatch({ type: 'SET_MODALS_OPEN', payload: null });
  };

  const onSubmit = (data: ElmoFormData) => {
    elmoExport(data);
  };

  if (!application?.candidate) return null;

  return (
    <StyledModal
      isOpen={modalsOpen === ModalType.ELMO}
      label="Elmo export modal"
      handleClose={handleClose}
      styleOverrides={styles.modalStyleOverrides}
    >
      <FormControl fullWidth >
        <Stack sx={styles.modalContainer}>
          <Stack sx={styles.modalTitle}>Export to Elmo</Stack>
          <Stack sx={styles.modalDoubleColumn}>
            <Controller
              name="firstname"
              control={control}
              render={({ field }) => (
                <FormTextField
                  {...field}
                  label="First name"
                  required
                  fullWidth
                  error={errors.firstname?.message}
                />
              )}
            />
            <Controller
              name="lastname"
              control={control}
              render={({ field }) => (
                <FormTextField
                  {...field}
                  label="Last name"
                  required
                  fullWidth
                  error={errors.lastname?.message}
                />
              )}
            />
          </Stack>
          <Stack sx={styles.modalDoubleColumn}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <FormTextField
                  {...field}
                  label="Email"
                  required
                  fullWidth
                  error={errors.email?.message}
                />
              )}
            />
            <Controller
              name="phone1"
              control={control}
              render={({ field }) => (
                <FormNumberField
                  {...field}
                  label="Mobile"
                  required
                  fullWidth
                  error={errors.phone1?.message}
                />
              )}
            />
          </Stack>
            <Controller
              name="department"
              control={control}
              render={({ field }) => (
                <FormSelectField
                  {...field}
                  label="Department"
                  required
                  styles={{ width: '8rem' }}
                  options={departments.map((dept) => ({
                    value: dept.id,
                    label: dept.title
                  }))}
                  error={errors.department?.message}
                />
              )}
            />
          <ModalFooterButtons
            primaryButtonText="Export"
            primaryButtonCallback={handleSubmit(onSubmit)}
            isLoading={exportingElmo}
            secondaryButtonText="Cancel"
            secondaryButtonCallback={handleClose}
            primaryButtonMinWidth="95px"
          />
        </Stack>
      </FormControl>
    </StyledModal>  
  );
}
